import React, { useState, useEffect, useRef } from "react";
import {
  User,
  Heart,
  ShoppingCart,
  Phone,
  Box,
  Search,
  Bell,
  ChevronDown,
  Home,
  Package,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import debounce from "lodash/debounce";
import { SERVER_URL } from "../constants";
import { useAuth } from "@clerk/clerk-react";

const Header = () => {
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cartItems, setCartItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const searchRef = useRef(null);
  const categoryRef = useRef(null);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const { isSignedIn, userId, signOut } = useAuth(); // Get userId from Clerk

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch current user's orders for notifications from /api/v1/orderu/:uid
  useEffect(() => {
    if (!isSignedIn || !userId) return;

    const fetchUserOrders = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/orderu/${userId}`);
        const currentOrders = response.data.data.filter(
          (order) => order.delivaryStatus !== "Delivered"
        ); // Filter non-delivered orders
        setNotifications(currentOrders);
      } catch (error) {
        console.error("Error fetching user orders:", error);
      }
    };

    fetchUserOrders();
    const interval = setInterval(fetchUserOrders, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [isSignedIn, userId]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setShowCategories(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load cart items
  useEffect(() => {
    const savedCartItems = localStorage.getItem("cartItems");
    if (savedCartItems) {
      setCartItems(Number(savedCartItems));
    }
  }, []);

  // Search handling
  const debouncedSearch = debounce(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_URL}/api/v1/search/search?q=${query}`
      );
      const filteredResults = response.data.filter((product) =>
        product.name.toLowerCase().startsWith(query.toLowerCase())
      );
      setSearchResults(filteredResults);
      setShowSearchResults(true);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query) {
      debouncedSearch(query);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchResults(false);
    }
  };

  const handleProductSelect = (productId) => {
    setShowSearchResults(false);
    setSearchQuery("");
    navigate(`/product/${productId}`);
  };

  const handleCategorySelect = (categoryId) => {
    setShowCategories(false);
    navigate(`/category/${categoryId}`);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="w-full font-sans">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-8 hidden sm:flex items-center">
        <div className="container mx-auto px-4 lg:px-6 flex justify-between items-center text-white text-sm">
          <div className="flex items-center">
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span className="text-xs sm:text-sm whitespace-nowrap">
                24/7 Support: <span className="font-medium hover:text-yellow-200 transition-colors">+91 99525 25181</span>
              </span>
            </span>
          </div>
          <div className="hidden sm:flex items-center divide-x divide-white/20">
            {isSignedIn ? (
              <>
                <Link to="/about" className="px-3 hover:text-yellow-200 text-xs transition-colors">About Us</Link>
                <Link to="/contact" className="px-3 hover:text-yellow-200 text-xs transition-colors">Contact Us</Link>
                <Link to="/faqs" className="px-3 hover:text-yellow-200 text-xs transition-colors">FAQ</Link>
                <button onClick={handleLogout} className="pl-3 hover:text-yellow-200 text-xs transition-colors">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/about" className="px-3 hover:text-yellow-200 text-xs transition-colors">About Us</Link>
                <Link to="/contact" className="px-3 hover:text-yellow-200 text-xs transition-colors">Contact Us</Link>
                <Link to="/faqs" className="px-3 hover:text-yellow-200 text-xs transition-colors">FAQ</Link>
                <Link to="/login" className="pl-3 hover:text-yellow-200 text-xs transition-colors">Login</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white py-3 sm:py-4 shadow-md border-b">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 group">
              <div className="flex items-center">
                <img
                  src="https://i.pinimg.com/736x/09/0f/55/090f5516b8b09acf34fd4d55517c2e24.jpg"
                  alt="Ecomify"
                  className="h-8 sm:h-10 w-auto"
                />
                <span className="hidden sm:block ml-2 text-xl sm:text-2xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors">
                  Ecomify
                </span>
              </div>
            </Link>

            {/* Search Bar */}
            <div className="flex-grow max-w-3xl relative" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <input
                  type="text"
                  className="w-full h-10 sm:h-11 pl-4 pr-12 text-sm border border-gray-200 rounded-full focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                  placeholder="Search for products (e.g. Earrings, watches, jewels)"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => setShowSearchResults(true)}
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 h-full px-4 text-gray-500 hover:text-emerald-600 transition-colors"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl max-h-96 overflow-y-auto border border-gray-100">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((product) => (
                      <div
                        key={product._id}
                        className="p-3 hover:bg-emerald-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-0 transition-colors"
                        onClick={() => handleProductSelect(product._id)}
                      >
                        {product.imgUrl && (
                          <img
                            src={product.imgUrl[0]}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-md bg-gray-100"
                          />
                        )}
                        <div className="flex-grow">
                          <div className="font-medium text-gray-800">{product.name}</div>
                          <div className="text-sm text-emerald-600 font-medium">₹{product.price}</div>
                        </div>
                        <div className="text-xs text-gray-600 bg-emerald-100 px-2 py-1 rounded-full">
                          {product.category}
                        </div>
                      </div>
                    ))
                  ) : searchQuery ? (
                    <div className="p-4 text-center text-gray-500">
                      <span className="text-sm">No products found</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Icons */}
            <div className="flex items-center gap-3 sm:gap-5">
              <button
                className="relative p-2 text-gray-700 hover:text-emerald-600 transition-colors hidden sm:block"
                onClick={() => navigate("/favorites")}
              >
                <Heart className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* Notification Icon */}
              {isSignedIn && (
                <div className="relative" ref={notificationRef}>
                  <button
                    className="p-2 text-gray-700 hover:text-emerald-600 transition-colors"
                    onClick={() => setShowNotifications(!showNotifications)}
                  >
                    <Bell className="w-6 h-6" />
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                        {notifications.length}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <div className="absolute z-50 right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 max-h-96 overflow-y-auto">
                      <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-lg">
                        <h3 className="text-sm font-semibold">Your Current Orders</h3>
                      </div>
                      {notifications.length > 0 ? (
                        notifications.map((order) => (
                          <div
                            key={order._id}
                            className="p-3 hover:bg-emerald-50 border-b border-gray-100 last:border-0 flex items-center gap-3 cursor-pointer transition-colors"
                            onClick={() => navigate(`/order/${order._id}`)}
                          >
                            <Package className="w-6 h-6 text-emerald-600" />
                            <div className="flex-grow">
                              <div className="text-sm font-medium text-gray-800">
                                {order.name}
                              </div>
                              <div className="text-xs text-gray-600">
                                Status: <span className="text-emerald-600 font-medium">{order.delivaryStatus}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                Updated: {formatDate(order.updatedAt || order.createdAt)}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          <span className="text-sm">No current orders</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isSignedIn ? (
                <>
                  <Link
                    to="/cart"
                    className="relative p-2 text-gray-700 hover:text-emerald-600 transition-colors"
                  >
                    <ShoppingCart className="w-6 h-6" />
                    {cartItems > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                        {cartItems}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/profile"
                    className="relative p-2 text-gray-700 hover:text-emerald-600 transition-colors hidden sm:block"
                  >
                    <User className="w-6 h-6" />
                  </Link>
                </>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  <User className="w-6 h-6" />
                  <span className="hidden sm:block text-sm font-medium">Login</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b hidden sm:block">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-8">
              {/* Categories Dropdown */}
              <button
                className="flex items-center gap-2 text-gray-700 hover:text-emerald-600 transition-colors py-3"
                onClick={() => navigate("/categories")}
              >
                <Box className="w-5 h-5" />
                <span className="font-medium">Categories</span>
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </button>

              {/* Navigation Links */}
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/about" className="text-gray-600 hover:text-emerald-600 transition-colors">
                  About Us
                </Link>
                <Link to="/contact" className="text-gray-600 hover:text-emerald-600 transition-colors">
                  Contact Us
                </Link>
              </nav>
            </div>

            {/* Language Selector */}
            <div className="flex items-center gap-2 group cursor-pointer">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/United-kingdom_flag_icon_round.svg/512px-United-kingdom_flag_icon_round.svg.png"
                alt="US"
                className="w-5 h-5 rounded-full"
              />
              <select className="bg-transparent border-none text-gray-700 cursor-pointer focus:outline-none">
                <option value="en">English</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-md sm:hidden z-50">
        <div className="flex justify-around items-center h-16">
          <Link to="/" className="flex flex-col items-center text-gray-600 hover:text-emerald-600 transition-colors">
            <Home className="w-6 h-6" />
            <span className="text-xs">Home</span>
          </Link>
          {isSignedIn ? (
            <>
              <Link
                to="/cart"
                className="flex flex-col items-center relative text-gray-600 hover:text-emerald-600 transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartItems > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {cartItems}
                  </span>
                )}
                <span className="text-xs">Cart</span>
              </Link>
              <div
                className="flex flex-col items-center relative text-gray-600 hover:text-emerald-600 transition-colors cursor-pointer"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="w-6 h-6" />
                {notifications.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {notifications.length}
                  </span>
                )}
                <span className="text-xs">Orders</span>
              </div>
              <Link
                to="/profile"
                className="flex flex-col items-center text-gray-600 hover:text-emerald-600 transition-colors"
              >
                <User className="w-6 h-6" />
                <span className="text-xs">Profile</span>
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="flex flex-col items-center text-gray-600 hover:text-emerald-600 transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                <span className="text-xs">Cart</span>
              </Link>
              <Link
                to="/login"
                className="flex flex-col items-center text-gray-600 hover:text-emerald-600 transition-colors"
              >
                <User className="w-6 h-6" />
                <span className="text-xs">Login</span>
              </Link>
            </>
          )}
          <Link
            to="/contact"
            className="flex flex-col items-center text-gray-600 hover:text-emerald-600 transition-colors"
          >
            <Phone className="w-6 h-6" />
            <span className="text-xs">Contact</span>
          </Link>
        </div>
      </div>

      {/* Mobile Notification Dropdown */}
      {showNotifications && isSignedIn && (
        <div className="fixed inset-0 bg-black/50 z-50 sm:hidden flex items-end">
          <div
            className="w-full bg-white rounded-t-lg p-4 max-h-[80vh] overflow-y-auto"
            ref={notificationRef}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-emerald-600">Your Current Orders</h3>
              <button
                className="text-gray-600 hover:text-emerald-600"
                onClick={() => setShowNotifications(false)}
              >
                Close
              </button>
            </div>
            {notifications.length > 0 ? (
              notifications.map((order) => (
                <div
                  key={order._id}
                  className="p-3 bg-emerald-50 rounded-lg mb-2 flex items-center gap-3 cursor-pointer"
                  onClick={() => navigate(`/order/${order._id}`)}
                >
                  <Package className="w-6 h-6 text-emerald-600" />
                  <div className="flex-grow">
                    <div className="text-sm font-medium text-gray-800">{order.name}</div>
                    <div className="text-xs text-gray-600">
                      Status: <span className="text-emerald-600 font-medium">{order.delivaryStatus}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Updated: {formatDate(order.updatedAt || order.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                <span className="text-sm">No current orders</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
